import axios from "axios";
import { getAttribution } from "../lib/tracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const catalogService = {
  async listVarieties(params = {}) {
    const query = new URLSearchParams();
    if (params.q) query.set("q", params.q);
    if (params.colors?.length) query.set("colors", params.colors.join(","));
    if (params.categories?.length) query.set("categories", params.categories.join(","));
    if (params.varieties?.length) query.set("varieties", params.varieties.join(","));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const { data } = await axios.get(`${API}/catalog/varieties${suffix}`, { timeout: 10000 });
    return data.varieties || [];
  },
  async getFacets() {
    const { data } = await axios.get(`${API}/catalog/facets`, { timeout: 10000 });
    return data;
  },
  async getVariety(id) {
    const { data } = await axios.get(`${API}/catalog/varieties/${id}`, { timeout: 10000 });
    return data;
  },
  async getRelated(id, limit = 4) {
    const { data } = await axios.get(`${API}/catalog/varieties/${id}/related?limit=${limit}`, { timeout: 10000 });
    return data.related || [];
  },
};

export const orderService = {
  async submit(payload) {
    const { data } = await axios.post(`${API}/orders/request`, payload, { timeout: 15000 });
    return data;
  },
};

// Resolves image sources coming from the catalog API:
// absolute URLs pass through, "/api/..." is served by the backend,
// anything else is a frontend public asset (e.g. /images/...).
export function resolveImageUrl(src) {
  if (!src) return "";
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  if (src.startsWith("/api/")) return `${BACKEND_URL}${src}`;
  return src;
}

const TOKEN_KEY = "tessa.admin.token";

export const authService = {
  getToken() { try { return window.sessionStorage.getItem(TOKEN_KEY) || ""; } catch (_) { return ""; } },
  setToken(t) { try { window.sessionStorage.setItem(TOKEN_KEY, t); } catch (_) {} },
  clear() { try { window.sessionStorage.removeItem(TOKEN_KEY); } catch (_) {} },
  async login(email, password) {
    const { data } = await axios.post(`${API}/admin/login`, { email, password }, {
      timeout: 10000, withCredentials: false,
    });
    if (data?.access_token) this.setToken(data.access_token);
    return data;
  },
  async me() {
    const token = this.getToken();
    if (!token) return null;
    try {
      const { data } = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
      });
      return data;
    } catch (_) { this.clear(); return null; }
  },
  async listOrders() {
    const token = this.getToken();
    const { data } = await axios.get(`${API}/admin/orders`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data.orders || [];
  },
  async logout() { this.clear(); },
};

export const adminCatalogService = {
  async listVarieties() {
    const token = authService.getToken();
    const { data } = await axios.get(`${API}/admin/varieties`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data.varieties || [];
  },
  async replaceVarietyImage(slug, slot, file) {
    const token = authService.getToken();
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.put(`${API}/admin/varieties/${slug}/images/${slot}`, form, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
    });
    return data;
  },
  async updateVarietyDescription(slug, description) {
    const token = authService.getToken();
    const { data } = await axios.put(`${API}/admin/varieties/${slug}`, { description_en: description }, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async updateVariety(slug, patch) {
    const token = authService.getToken();
    const { data } = await axios.put(`${API}/admin/varieties/${slug}`, patch, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async createVariety(payload) {
    const token = authService.getToken();
    const { data } = await axios.post(`${API}/admin/varieties`, payload, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async deleteVariety(slug) {
    const token = authService.getToken();
    await axios.delete(`${API}/admin/varieties/${slug}`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
  },
  async listCategories() {
    const token = authService.getToken();
    const { data } = await axios.get(`${API}/admin/categories`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data.categories || [];
  },
  async createCategory(label) {
    const token = authService.getToken();
    const { data } = await axios.post(`${API}/admin/categories`, { label }, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async deleteCategory(id) {
    const token = authService.getToken();
    await axios.delete(`${API}/admin/categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
  },
};

export const certificationService = {
  async list() {
    const { data } = await axios.get(`${API}/certifications`, { timeout: 10000 });
    return data.certifications || [];
  },
  async create(payload) {
    const token = authService.getToken();
    const { data } = await axios.post(`${API}/admin/certifications`, payload, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async update(slug, payload) {
    const token = authService.getToken();
    const { data } = await axios.put(`${API}/admin/certifications/${slug}`, payload, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async remove(slug) {
    const token = authService.getToken();
    await axios.delete(`${API}/admin/certifications/${slug}`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
  },
  async uploadLogo(slug, file) {
    const token = authService.getToken();
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.put(`${API}/admin/certifications/${slug}/logo`, form, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
    });
    return data;
  },
};

export const partnerService = {
  async list() {
    const { data } = await axios.get(`${API}/partners`, { timeout: 10000 });
    return data.partners || [];
  },
  async create(payload) {
    const token = authService.getToken();
    const { data } = await axios.post(`${API}/admin/partners`, payload, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async update(slug, payload) {
    const token = authService.getToken();
    const { data } = await axios.put(`${API}/admin/partners/${slug}`, payload, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async remove(slug) {
    const token = authService.getToken();
    await axios.delete(`${API}/admin/partners/${slug}`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
  },
  async uploadLogo(slug, file) {
    const token = authService.getToken();
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.put(`${API}/admin/partners/${slug}/logo`, form, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
    });
    return data;
  },
};

// About Us timeline milestones (client observations 27-oct)
export const milestoneService = {
  async list() {
    const { data } = await axios.get(`${API}/milestones`, { timeout: 10000 });
    return data.milestones || [];
  },
  async create(payload) {
    const token = authService.getToken();
    const { data } = await axios.post(`${API}/admin/milestones`, payload, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async update(id, payload) {
    const token = authService.getToken();
    const { data } = await axios.put(`${API}/admin/milestones/${id}`, payload, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async remove(id) {
    const token = authService.getToken();
    await axios.delete(`${API}/admin/milestones/${id}`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
  },
  async uploadImage(id, file) {
    const token = authService.getToken();
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.put(`${API}/admin/milestones/${id}/image`, form, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
    });
    return data;
  },
};


export const analyticsService = {
  async trackSearch(term, resultsCount) {
    try {
      await axios.post(`${API}/analytics/search`, { term, results_count: resultsCount }, { timeout: 8000 });
    } catch {
      /* analytics is best-effort */
    }
  },
  async topSearches() {
    const token = authService.getToken();
    const { data } = await axios.get(`${API}/admin/analytics/searches`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data.searches || [];
  },
  async interactions() {
    const token = authService.getToken();
    const { data } = await axios.get(`${API}/admin/analytics/interactions`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async dashboard() {
    const token = authService.getToken();
    const { data } = await axios.get(`${API}/admin/dashboard`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
};

export const popupService = {
  async getPublic() {
    const { data } = await axios.get(`${API}/popup`, { timeout: 10000 });
    return data;
  },
  async getAdmin() {
    const token = authService.getToken();
    const { data } = await axios.get(`${API}/admin/popup`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async save(config) {
    const token = authService.getToken();
    const { data } = await axios.put(`${API}/admin/popup`, config, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async uploadImage(file) {
    const token = authService.getToken();
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.put(`${API}/admin/popup/image`, form, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
    });
    return data;
  },
  async subscribe(payload) {
    const { data } = await axios.post(`${API}/popup/subscribe`, { ...payload, ...getAttribution() }, { timeout: 10000 });
    return data;
  },
};

export const articleService = {
  async list() {
    const { data } = await axios.get(`${API}/articles`, { timeout: 10000 });
    return data.articles || [];
  },
  async create(payload) {
    const token = authService.getToken();
    const { data } = await axios.post(`${API}/admin/articles`, payload, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async update(slug, payload) {
    const token = authService.getToken();
    const { data } = await axios.put(`${API}/admin/articles/${slug}`, payload, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async remove(slug) {
    const token = authService.getToken();
    await axios.delete(`${API}/admin/articles/${slug}`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
  },
  async uploadImage(slug, file) {
    const token = authService.getToken();
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.put(`${API}/admin/articles/${slug}/image`, form, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
    });
    return data;
  },
};

export const seasonPicksService = {  async getPublic() {
    const { data } = await axios.get(`${API}/season-picks`, { timeout: 10000 });
    return data;
  },
  async getAdmin() {
    const token = authService.getToken();
    const { data } = await axios.get(`${API}/admin/season-picks`, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
  async save(config) {
    const token = authService.getToken();
    const { data } = await axios.put(`${API}/admin/season-picks`, config, {
      headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
    });
    return data;
  },
};

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}
